/*
  Shared discount-code helper, used by both the cart drawer below and the
  /cart page section. Shopify exposes no first-class AJAX endpoint for
  discount codes: `cart/update.js` accepts a `discount` field on most shops,
  and where it does not the `/discount/<codes>` route sets the same cookie.
  Both take a comma-separated list, so one call replaces the whole set.
*/
window.wovenCartDiscounts = window.wovenCartDiscounts || (() => {
  const cartRoot = window.Shopify?.routes?.root || '/';
  const clean = (code) => String(code || '').trim();
  const key = (code) => clean(code).toUpperCase();

  const get = async () => {
    const response = await fetch(`${cartRoot}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('We couldn’t read your bag.');
    return response.json();
  };

  const codesOn = (cart) => (cart?.discount_codes || []).map((entry) => entry.code);
  const applied = (cart) => (cart?.discount_codes || []).filter((entry) => entry.applicable).map((entry) => entry.code);

  const matches = (cart, wanted) => {
    const current = codesOn(cart).map(key);
    return wanted.length === current.length && wanted.map(key).every((code) => current.includes(code));
  };

  const set = async (codes) => {
    const list = codes.map(clean).filter(Boolean);
    let cart = null;
    try {
      const response = await fetch(`${cartRoot}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: list.join(',') }),
      });
      if (response.ok) cart = await response.json();
    } catch (_) {
      cart = null;
    }
    if (cart && matches(cart, list)) return cart;
    if (list.length > 0) {
      await fetch(`${cartRoot}discount/${encodeURIComponent(list.join(','))}`, {
        headers: { Accept: 'text/html' },
      }).catch(() => {});
    }
    return get();
  };

  const add = async (cart, code) => set([...applied(cart), code]);
  const remove = async (cart, code) => set(applied(cart).filter((entry) => key(entry) !== key(code)));

  // Returns a customer-facing problem string, or null when the change stuck.
  const problem = (cart, code, mode) => {
    const entry = (cart?.discount_codes || []).find((candidate) => key(candidate.code) === key(code));
    if (mode === 'remove') {
      return entry ? `We couldn’t remove ${key(code)}. You can still edit it at checkout.` : null;
    }
    if (entry && entry.applicable) return null;
    return `${key(code)} can’t be applied to this bag.`;
  };

  return { get, applied, set, add, remove, problem };
})();

(() => {
  const drawer = document.querySelector('[data-woven-cart-drawer]');
  if (!drawer || drawer.dataset.ready === 'true') return;
  drawer.dataset.ready = 'true';

  const root = window.Shopify?.routes?.root || '/';
  const threshold = Number(drawer.dataset.freeShippingThreshold || 15000);
  let currentCart = null;
  let returnFocus = null;

  const money = (cents, currency = currentCart?.currency || 'USD') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const updateHeaderCount = (count) => {
    document.querySelectorAll('.woven-header__cart-count').forEach((node) => { node.textContent = `(${count})`; });
    document.querySelectorAll('.woven-header__cart').forEach((node) => node.setAttribute('aria-label', `Cart with ${count} items`));
  };

  const shippingBlock = (cart) => {
    const block = element('div', 'woven-cart-drawer__shipping');
    block.dataset.cartDrawerShipping = '';
    const copy = element('p');
    const remaining = threshold - cart.total_price;
    if (remaining > 0) {
      copy.append('You’re ');
      copy.append(element('strong', '', money(remaining, cart.currency)));
      copy.append(' away from free shipping.');
    } else {
      copy.append(element('strong', '', 'Free shipping unlocked.'));
    }
    const meter = element('div', 'woven-cart-drawer__meter');
    const progress = Math.min(100, threshold > 0 ? (cart.total_price / threshold) * 100 : 100);
    meter.setAttribute('role', 'progressbar');
    meter.setAttribute('aria-label', 'Free shipping progress');
    meter.setAttribute('aria-valuemin', '0');
    meter.setAttribute('aria-valuemax', '100');
    meter.setAttribute('aria-valuenow', String(Math.round(progress)));
    const fill = element('span');
    fill.style.width = `${progress}%`;
    meter.append(fill);
    block.append(copy, meter);
    return block;
  };

  const lineItem = (item, currency) => {
    const article = element('article', 'woven-cart-drawer-item');
    article.dataset.cartDrawerLine = '';
    article.dataset.lineKey = item.key;

    const imageLink = element('a', 'woven-cart-drawer-item__image');
    imageLink.href = item.url;
    if (item.image) {
      const image = element('img');
      image.src = `${item.image}${item.image.includes('?') ? '&' : '?'}width=240`;
      image.alt = item.product_title;
      image.width = 80;
      image.height = 100;
      image.loading = 'lazy';
      imageLink.append(image);
    }

    const details = element('div', 'woven-cart-drawer-item__details');
    const topline = element('div', 'woven-cart-drawer-item__topline');
    const heading = element('h3');
    const title = element('a', '', item.product_title);
    title.href = item.url;
    heading.append(title);
    topline.append(heading, element('span', '', money(item.final_line_price, currency)));
    details.append(topline);
    if (item.variant_title) details.append(element('p', '', item.variant_title));

    const actions = element('div', 'woven-cart-drawer-item__actions');
    const stepper = element('div', 'woven-cart-drawer-item__stepper');
    const minus = element('button', '', '−');
    minus.type = 'button';
    minus.dataset.cartDrawerMinus = '';
    minus.setAttribute('aria-label', `Decrease ${item.product_title} quantity`);
    const quantity = element('span', '', String(item.quantity));
    quantity.setAttribute('aria-live', 'polite');
    const plus = element('button', '', '+');
    plus.type = 'button';
    plus.dataset.cartDrawerPlus = '';
    plus.setAttribute('aria-label', `Increase ${item.product_title} quantity`);
    stepper.append(minus, quantity, plus);
    const remove = element('button', 'woven-cart-drawer-item__remove', 'Remove');
    remove.type = 'button';
    remove.dataset.cartDrawerRemove = '';
    actions.append(stepper, remove);
    details.append(actions);
    article.append(imageLink, details);
    return article;
  };

  const emptyState = () => {
    const empty = element('div', 'woven-cart-drawer__empty');
    empty.dataset.cartDrawerEmpty = '';
    empty.append(element('p', '', 'Your bag is empty.'));
    const shop = element('a', '', 'Shop new arrivals');
    shop.href = `${root}collections/all`;
    empty.append(shop);
    const categories = element('div');
    const baskets = element('a', '', 'Baskets & storage');
    baskets.href = `${root}collections/baskets-storage`;
    const pillows = element('a', '', 'Pillows & throws');
    pillows.href = `${root}collections/pillows-throws`;
    categories.append(baskets, pillows);
    empty.append(categories);
    return empty;
  };

  const renderTotals = (cart) => {
    const totals = drawer.querySelector('[data-cart-drawer-totals]');
    if (!totals) return;
    totals.replaceChildren();

    const subtotal = element('div', 'woven-cart-drawer__subtotal');
    const subtotalValue = element('strong', '', money(cart.original_total_price, cart.currency));
    subtotalValue.dataset.cartDrawerSubtotal = '';
    subtotal.append(element('span', '', 'Subtotal'), subtotalValue);
    totals.append(subtotal);

    if (cart.total_discount > 0) {
      const appliedCodes = (cart.discount_codes || []).filter((entry) => entry.applicable).map((entry) => entry.code);
      const discountLabel = appliedCodes.length === 1 ? `Discount (${String(appliedCodes[0]).toUpperCase()})` : appliedCodes.length > 1 ? 'Discounts' : 'Discount';
      const row = element('div', 'woven-cart-drawer__discount-line');
      row.append(
        element('span', '', discountLabel),
        element('strong', '', `\u2212${money(cart.total_discount, cart.currency)}`)
      );
      totals.append(row);
    }

    const total = element('div', 'woven-cart-drawer__total');
    total.append(element('span', '', 'Total'), element('strong', '', money(cart.total_price, cart.currency)));
    totals.append(total);
  };

  const renderDiscounts = (cart) => {
    const tags = drawer.querySelector('[data-cart-drawer-discount-tags]');
    if (!tags) return;
    tags.replaceChildren();

    const codes = cart.discount_codes || [];
    codes.forEach((entry) => {
      const tag = element('li');
      if (!entry.applicable) tag.dataset.inapplicable = '';
      tag.append(element('span', '', entry.code));
      if (!entry.applicable) tag.append(element('em', '', 'not applicable'));
      const remove = element('button', '', '\u00d7');
      remove.type = 'button';
      remove.dataset.cartDrawerDiscountRemove = entry.code;
      remove.setAttribute('aria-label', `Remove discount ${entry.code}`);
      tag.append(remove);
      tags.append(tag);
    });

    const message = drawer.querySelector('[data-cart-drawer-discount-message]');
    if (message) {
      message.textContent = '';
      message.hidden = true;
    }
  };

  const render = (cart, announcement = '') => {
    currentCart = cart;
    drawer.querySelector('[data-cart-drawer-count]').textContent = String(cart.item_count);
    updateHeaderCount(cart.item_count);

    const content = drawer.querySelector('[data-cart-drawer-content]');
    content.replaceChildren();
    if (cart.item_count > 0) {
      if (threshold > 0) content.append(shippingBlock(cart));
      const lines = element('div', 'woven-cart-drawer__lines');
      lines.dataset.cartDrawerLines = '';
      cart.items.forEach((item) => lines.append(lineItem(item, cart.currency)));
      content.append(lines);
    } else {
      content.append(emptyState());
    }

    const footer = drawer.querySelector('[data-cart-drawer-footer]');
    footer.hidden = cart.item_count === 0;
    renderTotals(cart);
    renderDiscounts(cart);
    const note = drawer.querySelector('[data-cart-drawer-note]');
    if (note && document.activeElement !== note) note.value = cart.note || '';
    const live = drawer.querySelector('[data-cart-drawer-live]');
    live.textContent = announcement;
    const error = drawer.querySelector('[data-cart-drawer-error]');
    error.hidden = true;
    error.textContent = '';
  };

  const setDiscountBusy = (trigger, busy) => {
    if (!trigger) return;
    trigger.disabled = busy;
    trigger.setAttribute('aria-busy', String(busy));
    if (trigger.matches('[data-cart-drawer-discount-apply]')) {
      if (busy) {
        trigger.dataset.discountLoading = 'true';
        trigger.setAttribute('aria-label', 'Applying discount code');
      } else {
        delete trigger.dataset.discountLoading;
        trigger.removeAttribute('aria-label');
      }
    }
  };

  const request = async (path, options) => {
    drawer.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${root}${path}`, options);
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'We couldn’t update your bag.');
      return result;
    } finally {
      drawer.removeAttribute('aria-busy');
    }
  };

  const refresh = async (announcement = '') => {
    try {
      const cart = await request('cart.js', { headers: { Accept: 'application/json' } });
      render(cart, announcement);
    } catch (error) {
      const target = drawer.querySelector('[data-cart-drawer-error]');
      target.textContent = error.message;
      target.hidden = false;
    }
  };

  const changeDiscount = async (mode, code, trigger) => {
    const api = window.wovenCartDiscounts;
    const message = drawer.querySelector('[data-cart-drawer-discount-message]');
    if (!api) return;
    setDiscountBusy(trigger, true);
    try {
      const current = await api.get();
      const cart = mode === 'add' ? await api.add(current, code) : await api.remove(current, code);
      const problem = api.problem(cart, code, mode);
      render(cart, problem ? '' : mode === 'add' ? 'Discount applied.' : 'Discount removed.');
      if (problem && message) {
        message.textContent = problem;
        message.hidden = false;
      } else if (mode === 'add') {
        const input = drawer.querySelector('[data-cart-drawer-discount-input]');
        if (input) input.value = '';
        const details = drawer.querySelector('[data-cart-drawer-discount]');
        if (details) details.open = false;
      }
    } catch (error) {
      const target = drawer.querySelector('[data-cart-drawer-error]');
      target.textContent = error.message;
      target.hidden = false;
    } finally {
      if (trigger?.isConnected) setDiscountBusy(trigger, false);
    }
  };

  const open = (trigger, announcement = '') => {
    returnFocus = trigger || document.activeElement;
    if (!drawer.open) drawer.showModal();
    drawer.querySelector('[data-cart-drawer-close]')?.focus();
    refresh(announcement);
  };

  const close = () => {
    if (drawer.open) drawer.close();
  };

  document.addEventListener('click', async (event) => {
    const openTrigger = event.target.closest('[data-cart-drawer-open]');
    if (openTrigger) {
      event.preventDefault();
      open(openTrigger);
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]')) {
      close();
      return;
    }

    const discountApply = event.target.closest('[data-cart-drawer-discount-apply]');
    if (discountApply) {
      const input = drawer.querySelector('[data-cart-drawer-discount-input]');
      const code = (input?.value || '').trim();
      if (!code) {
        input?.focus();
        return;
      }
      await changeDiscount('add', code, discountApply);
      return;
    }

    const discountRemove = event.target.closest('[data-cart-drawer-discount-remove]');
    if (discountRemove) {
      await changeDiscount('remove', discountRemove.dataset.cartDrawerDiscountRemove, discountRemove);
      return;
    }

    const line = event.target.closest('[data-cart-drawer-line]');
    if (!line) return;
    const quantityNode = line.querySelector('.woven-cart-drawer-item__stepper span');
    const currentQuantity = Number(quantityNode?.textContent || 0);
    let nextQuantity = currentQuantity;
    if (event.target.closest('[data-cart-drawer-minus]')) nextQuantity = Math.max(0, currentQuantity - 1);
    else if (event.target.closest('[data-cart-drawer-plus]')) nextQuantity = currentQuantity + 1;
    else if (event.target.closest('[data-cart-drawer-remove]')) nextQuantity = 0;
    else return;

    try {
      const cart = await request('cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.dataset.lineKey, quantity: nextQuantity }),
      });
      render(cart, 'Your bag has been updated.');
    } catch (error) {
      const target = drawer.querySelector('[data-cart-drawer-error]');
      target.textContent = error.message;
      target.hidden = false;
    }
  });

  drawer.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    if (!event.target.matches('[data-cart-drawer-discount-input]')) return;
    event.preventDefault();
    drawer.querySelector('[data-cart-drawer-discount-apply]')?.click();
  });

  drawer.addEventListener('click', (event) => {
    if (event.target === drawer) close();
  });

  drawer.addEventListener('close', () => {
    if (returnFocus?.isConnected) returnFocus.focus();
  });

  drawer.querySelector('[data-cart-drawer-note]')?.addEventListener('change', async (event) => {
    try {
      const cart = await request('cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: event.target.value }),
      });
      render(cart, 'Your order note has been saved.');
    } catch (error) {
      const target = drawer.querySelector('[data-cart-drawer-error]');
      target.textContent = error.message;
      target.hidden = false;
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('.woven-product-card__form');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    button.disabled = true;
    try {
      await request('cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      open(button, 'Added to your bag.');
    } catch (error) {
      button.disabled = false;
      button.textContent = error.message;
    } finally {
      button.disabled = false;
    }
  });

  document.addEventListener('woven:cart:open', (event) => open(event.detail?.trigger, event.detail?.announcement || ''));

  if (currentCart === null) refresh();
})();
